import { create } from 'zustand';

// Simple UUID generator fallback
const generateId = () => typeof crypto !== 'undefined' && crypto.randomUUID ? crypto.randomUUID() : Math.random().toString(36).substring(2, 15);

const clampDuration = (value) => {
  const n = parseInt(value, 10);
  if (!Number.isFinite(n) || n < 1) return 1;
  return Math.min(52, n);
};

const emptySlots = () => Array.from({ length: 7 }).map(() => ({
  slot_id: generateId(),
  kind: 'rest',
  workout_id: null,
  notes: ''
}));

const cloneSlotsFrom = (slots) => (slots || emptySlots()).map((s) => ({
  slot_id: generateId(),
  kind: s.kind || 'rest',
  workout_id: s.workout_id || null,
  notes: s.notes || ''
}));

const makeWeek = (week_number, slots) => {
  const week_id = generateId();
  return {
    week_id,
    week_number,
    focus: '',
    slots: slots || emptySlots()
  };
};

const slotsMatch = (a, b) => {
  const left = a || [];
  const right = b || [];
  if (left.length !== right.length) return false;
  return left.every((slot, i) => {
    const other = right[i] || {};
    return (slot.kind || 'rest') === (other.kind || 'rest')
      && (slot.workout_id || null) === (other.workout_id || null)
      && (slot.notes || '') === (other.notes || '');
  });
};

/**
 * Duration is how long the plan runs.
 * Repeat week is a single week template cycled for that whole run — it does not
 * reset the run length to 1, and it does not add a row per week.
 * With repeat off, duration is the number of distinct weeks on the board.
 */
const syncWeekState = (state, durationOverride, repeatingOverride, { reconcile = false } = {}) => {
  const target = clampDuration(durationOverride ?? state.meta.duration_weeks);
  let isRepeating = repeatingOverride ?? !!state.meta.is_repeating;
  let weekOrder = [...(state.weekOrder || [])];
  const weeks = { ...(state.weeks || {}) };

  if (reconcile && isRepeating && weekOrder.length > 1) {
    const template = weeks[weekOrder[0]]?.slots;
    const allMatch = weekOrder.every((id) => slotsMatch(weeks[id]?.slots, template));
    if (!allMatch) isRepeating = false;
  }

  const rowTarget = isRepeating ? 1 : target;
  const sourceSlots = weekOrder[0] && weeks[weekOrder[0]] ? weeks[weekOrder[0]].slots : null;

  if (weekOrder.length < rowTarget) {
    for (let i = weekOrder.length; i < rowTarget; i++) {
      const week = makeWeek(i + 1, sourceSlots ? cloneSlotsFrom(sourceSlots) : emptySlots());
      weekOrder.push(week.week_id);
      weeks[week.week_id] = week;
    }
  } else if (weekOrder.length > rowTarget) {
    const removed = weekOrder.slice(rowTarget);
    weekOrder = weekOrder.slice(0, rowTarget);
    removed.forEach((id) => { delete weeks[id]; });
  }

  if (!weekOrder.length) {
    const week = makeWeek(1, emptySlots());
    weekOrder = [week.week_id];
    weeks[week.week_id] = week;
  }

  weekOrder.forEach((id, index) => {
    if (weeks[id]) weeks[id] = { ...weeks[id], week_number: index + 1 };
  });

  return {
    weekOrder,
    weeks,
    duration: isRepeating ? target : weekOrder.length,
    isRepeating
  };
};

const useTrainingPlanStore = create((set, get) => ({
  meta: {
    template_id: '',
    name: '',
    duration_weeks: 1,
    is_repeating: true,
    intensity_level: 'moderate-high',
    focus: '',
    pre_workout_notes: ''
  },
  assignment: {
    user_code: '',
    status: 'active',
    active_from: new Date().toISOString().split('T')[0],
    active_until: '',
    goal: 'strength_training',
    difficulty_level: 'beginner',
    notes: ''
  },
  workoutOrder: [],
  workouts: {},
  weekOrder: [],
  weeks: {},
  exercises: {},
  sets: {},
  ui: {
    expandedWorkoutId: null,
    expandedExerciseId: null,
    searchOpenForWorkoutId: null,
    editingWorkoutId: null,
    drawerLeaf: 'workout'
  },

  // Actions - Initialization
  initPlan: (planData) => {
    const next = { ...planData };
    if (next.workoutOrder?.length > 0 && !next.ui?.expandedWorkoutId) {
      next.ui = { ...(next.ui || {}), expandedWorkoutId: next.workoutOrder[0] };
    }
    const synced = syncWeekState({
      ...next,
      meta: next.meta || {},
      weekOrder: next.weekOrder || [],
      weeks: next.weeks || {}
    }, undefined, undefined, { reconcile: true });
    set({
      ...next,
      meta: {
        ...(next.meta || {}),
        duration_weeks: synced.duration,
        is_repeating: synced.isRepeating
      },
      weekOrder: synced.weekOrder,
      weeks: synced.weeks
    });
  },

  syncWeeksToDuration: () => set((state) => {
    const synced = syncWeekState(state);
    return {
      meta: { ...state.meta, duration_weeks: synced.duration },
      weekOrder: synced.weekOrder,
      weeks: synced.weeks
    };
  }),

  // Actions - Meta / Assignment
  updateMeta: (updates) => set((state) => {
    const nextMeta = { ...state.meta, ...updates };
    if (updates.duration_weeks != null) {
      nextMeta.duration_weeks = clampDuration(updates.duration_weeks);
    }
    const shouldSync = updates.duration_weeks != null || updates.is_repeating != null;
    if (!shouldSync) return { meta: nextMeta };
    // Repeat week: the number is the run length. It must not grow or collapse the board.
    if (updates.duration_weeks != null && updates.is_repeating == null && nextMeta.is_repeating) {
      return { meta: nextMeta };
    }
    const synced = syncWeekState(
      { ...state, meta: nextMeta },
      nextMeta.duration_weeks,
      nextMeta.is_repeating
    );
    return {
      meta: { ...nextMeta, duration_weeks: synced.duration, is_repeating: synced.isRepeating },
      weekOrder: synced.weekOrder,
      weeks: synced.weeks
    };
  }),
  updateAssignment: (updates) => set((state) => ({
    assignment: { ...state.assignment, ...updates }
  })),

  // Actions - Weeks & Slots
  addWeek: () => set((state) => {
    const week_id = generateId();
    const newWeek = {
      week_id,
      week_number: state.weekOrder.length + 1,
      focus: '',
      slots: Array.from({ length: 7 }).map(() => ({
        slot_id: generateId(),
        kind: 'rest', // 'empty' | 'workout' | 'rest'
        workout_id: null,
        notes: ''
      }))
    };
    return {
      weekOrder: [...state.weekOrder, week_id],
      weeks: { ...state.weeks, [week_id]: newWeek }
    };
  }),

  removeWeek: (week_id) => set((state) => {
    const newWeekOrder = state.weekOrder.filter(id => id !== week_id);
    const { [week_id]: removedWeek, ...remainingWeeks } = state.weeks;
    
    // update week numbers
    newWeekOrder.forEach((id, index) => {
      remainingWeeks[id] = { ...remainingWeeks[id], week_number: index + 1 };
    });

    return {
      weekOrder: newWeekOrder,
      weeks: remainingWeeks
    };
  }),

  updateWeek: (week_id, updates) => set((state) => ({
    weeks: {
      ...state.weeks,
      [week_id]: { ...state.weeks[week_id], ...updates }
    }
  })),

  assignWorkoutToSlot: (week_id, slotIndex, workout_id) => set((state) => {
    const week = state.weeks[week_id];
    if (!week) return state;
    const newSlots = [...week.slots];
    newSlots[slotIndex] = { ...newSlots[slotIndex], kind: 'workout', workout_id, notes: '' };
    return {
      weeks: { ...state.weeks, [week_id]: { ...week, slots: newSlots } }
    };
  }),

  assignRestToSlot: (week_id, slotIndex) => set((state) => {
    const week = state.weeks[week_id];
    if (!week) return state;
    const newSlots = [...week.slots];
    newSlots[slotIndex] = { ...newSlots[slotIndex], kind: 'rest', workout_id: null };
    return {
      weeks: { ...state.weeks, [week_id]: { ...week, slots: newSlots } }
    };
  }),

  assignNutritionToSlot: (week_id, slotIndex) => set((state) => {
    const week = state.weeks[week_id];
    if (!week) return state;
    const newSlots = [...week.slots];
    newSlots[slotIndex] = { ...newSlots[slotIndex], kind: 'nutrition', workout_id: null };
    return {
      weeks: { ...state.weeks, [week_id]: { ...week, slots: newSlots } }
    };
  }),

  clearSlot: (week_id, slotIndex) => set((state) => {
    const week = state.weeks[week_id];
    if (!week) return state;
    const newSlots = [...week.slots];
    newSlots[slotIndex] = { ...newSlots[slotIndex], kind: 'rest', workout_id: null, notes: '' };
    return {
      weeks: { ...state.weeks, [week_id]: { ...week, slots: newSlots } }
    };
  }),

  updateSlotNotes: (week_id, slotIndex, notes) => set((state) => {
    const week = state.weeks[week_id];
    if (!week) return state;
    const newSlots = [...week.slots];
    newSlots[slotIndex] = { ...newSlots[slotIndex], notes };
    return {
      weeks: { ...state.weeks, [week_id]: { ...week, slots: newSlots } }
    };
  }),

  moveWorkoutToSlot: (source_week, source_slot, dest_week, dest_slot) => set((state) => {
    const srcW = state.weeks[source_week];
    const dstW = state.weeks[dest_week];
    if (!srcW || !dstW) return state;

    const sourceSlotItem = srcW.slots[source_slot];
    const destSlotItem = dstW.slots[dest_slot];

    const newSrcSlots = [...srcW.slots];
    const newDstSlots = source_week === dest_week ? newSrcSlots : [...dstW.slots];

    // Swap the slot items exactly
    newSrcSlots[source_slot] = destSlotItem;
    newDstSlots[dest_slot] = sourceSlotItem;

    return {
      weeks: {
        ...state.weeks,
        [source_week]: { ...srcW, slots: newSrcSlots },
        [dest_week]: { ...dstW, slots: newDstSlots }
      }
    };
  }),

  // Actions - Workouts
  addWorkout: () => set((state) => {
    const workout_id = generateId();
    const newWorkout = {
      workout_id,
      day_name: `Workout ${state.workoutOrder.length + 1}`,
      recommended_day: 'monday',
      order: state.workoutOrder.length + 1,
      exerciseIds: []
    };
    return {
      workoutOrder: [...state.workoutOrder, workout_id],
      workouts: { ...state.workouts, [workout_id]: newWorkout },
      ui: { ...state.ui, expandedWorkoutId: workout_id }
    };
  }),

  createWorkoutForSlot: (week_id, slotIndex, language) => set((state) => {
    const workout_id = generateId();
    const dayNameBase = language === 'hebrew' ? 'אימון חדש' : 'New Workout';
    const newWorkout = {
      workout_id,
      day_name: dayNameBase,
      recommended_day: 'monday',
      order: state.workoutOrder.length + 1,
      exerciseIds: []
    };
    
    const week = state.weeks[week_id];
    const newSlots = [...week.slots];
    newSlots[slotIndex] = { ...newSlots[slotIndex], kind: 'workout', workout_id, notes: '' };

    return {
      workoutOrder: [...state.workoutOrder, workout_id],
      workouts: { ...state.workouts, [workout_id]: newWorkout },
      weeks: { ...state.weeks, [week_id]: { ...week, slots: newSlots } },
      ui: { ...state.ui, editingWorkoutId: workout_id, searchOpenForWorkoutId: workout_id, expandedExerciseId: null }
    };
  }),
  
  updateWorkout: (workout_id, updates) => set((state) => ({
    workouts: {
      ...state.workouts,
      [workout_id]: { ...state.workouts[workout_id], ...updates }
    }
  })),

  removeWorkout: (workout_id) => set((state) => {
    const { [workout_id]: removedWorkout, ...remainingWorkouts } = state.workouts;
    const newWorkoutOrder = state.workoutOrder.filter(id => id !== workout_id);
    
    // Clean up exercises and sets
    const remainingExercises = { ...state.exercises };
    const remainingSets = { ...state.sets };
    
    removedWorkout.exerciseIds.forEach(exId => {
      const ex = remainingExercises[exId];
      if (ex) {
        ex.setIds.forEach(setId => {
          delete remainingSets[setId];
        });
        delete remainingExercises[exId];
      }
    });

    // Also clear from any week slots
    const newWeeks = { ...state.weeks };
    state.weekOrder.forEach(wId => {
      const week = newWeeks[wId];
      let updatedSlots = false;
      const slots = week.slots.map(slot => {
        if (slot.workout_id === workout_id) {
          updatedSlots = true;
          return { ...slot, kind: 'rest', workout_id: null, notes: '' };
        }
        return slot;
      });
      if (updatedSlots) {
        newWeeks[wId] = { ...week, slots };
      }
    });

    return {
      workoutOrder: newWorkoutOrder,
      workouts: remainingWorkouts,
      exercises: remainingExercises,
      sets: remainingSets,
      weeks: newWeeks
    };
  }),
  
  duplicateWorkout: (workout_id) => set((state) => {
    const original = state.workouts[workout_id];
    if (!original) return state;

    const newWorkoutId = generateId();
    const newWorkout = {
      ...original,
      workout_id: newWorkoutId,
      day_name: `${original.day_name} (Copy)`,
      exerciseIds: []
    };
    
    const newExercises = { ...state.exercises };
    const newSets = { ...state.sets };

    original.exerciseIds.forEach(exId => {
      const originalEx = state.exercises[exId];
      if (originalEx) {
        const newExId = generateId();
        newWorkout.exerciseIds.push(newExId);
        
        const newEx = {
          ...originalEx,
          exercise_instance_id: newExId,
          setIds: []
        };
        newExercises[newExId] = newEx;
        
        originalEx.setIds.forEach(setId => {
          const originalSet = state.sets[setId];
          if (originalSet) {
            const newSetId = generateId();
            newEx.setIds.push(newSetId);
            newSets[newSetId] = { ...originalSet, set_id: newSetId };
          }
        });
      }
    });
    
    const index = state.workoutOrder.indexOf(workout_id);
    const newWorkoutOrder = [...state.workoutOrder];
    newWorkoutOrder.splice(index + 1, 0, newWorkoutId);

    return {
      workoutOrder: newWorkoutOrder,
      workouts: { ...state.workouts, [newWorkoutId]: newWorkout },
      exercises: newExercises,
      sets: newSets
    };
  }),

  reorderWorkouts: (startIndex, endIndex) => set((state) => {
    const result = Array.from(state.workoutOrder);
    const [removed] = result.splice(startIndex, 1);
    result.splice(endIndex, 0, removed);
    
    // update order field
    const newWorkouts = { ...state.workouts };
    result.forEach((id, i) => {
      if (newWorkouts[id]) {
        newWorkouts[id] = { ...newWorkouts[id], order: i + 1 };
      }
    });
    
    return { workoutOrder: result, workouts: newWorkouts };
  }),

  // Actions - Exercises
  addExerciseToWorkout: (workout_id, exerciseLibraryItem) => set((state) => {
    const workout = state.workouts[workout_id];
    if (!workout) return state;

    const exercise_instance_id = generateId();
    const set_id = generateId();

    const newExercise = {
      exercise_instance_id,
      global_exercise_id: exerciseLibraryItem.id || exerciseLibraryItem.exercise_id || null,
      exercise_name: exerciseLibraryItem.exercise_name || exerciseLibraryItem.exercise_name_he || '',
      order: workout.exerciseIds.length + 1,
      rest_seconds: 90,
      coach_notes: '',
      setIds: [set_id]
    };

    return {
      workouts: {
        ...state.workouts,
        [workout_id]: { ...workout, exerciseIds: [...workout.exerciseIds, exercise_instance_id] }
      },
      exercises: { ...state.exercises, [exercise_instance_id]: newExercise },
      sets: {
        ...state.sets,
        [set_id]: {
          set_id,
          set_index: 1,
          set_type: 'working',
          min_reps: 10,
          max_reps: 10,
          target_rpe: 5,
          target_weight: ''
        }
      },
      ui: { 
        ...state.ui, 
        expandedWorkoutId: workout_id, 
        searchOpenForWorkoutId: null,
        expandedExerciseId: exercise_instance_id,
        drawerLeaf: 'exercise'
      }
    };
  }),

  updateExercise: (exercise_instance_id, updates) => set((state) => ({
    exercises: {
      ...state.exercises,
      [exercise_instance_id]: { ...state.exercises[exercise_instance_id], ...updates }
    }
  })),

  removeExercise: (workout_id, exercise_instance_id) => set((state) => {
    const workout = state.workouts[workout_id];
    if (!workout) return state;
    
    const newExerciseIds = workout.exerciseIds.filter(id => id !== exercise_instance_id);
    
    const { [exercise_instance_id]: removedExercise, ...remainingExercises } = state.exercises;
    const remainingSets = { ...state.sets };
    if (removedExercise) {
      removedExercise.setIds.forEach(setId => {
        delete remainingSets[setId];
      });
    }

    return {
      workouts: {
        ...state.workouts,
        [workout_id]: { ...workout, exerciseIds: newExerciseIds }
      },
      exercises: remainingExercises,
      sets: remainingSets
    };
  }),

  reorderExercises: (workout_id, startIndex, endIndex) => set((state) => {
    const workout = state.workouts[workout_id];
    if (!workout) return state;

    const result = Array.from(workout.exerciseIds);
    const [removed] = result.splice(startIndex, 1);
    result.splice(endIndex, 0, removed);
    
    // update order
    const newExercises = { ...state.exercises };
    result.forEach((id, i) => {
      if (newExercises[id]) {
        newExercises[id] = { ...newExercises[id], order: i + 1 };
      }
    });

    return {
      workouts: {
        ...state.workouts,
        [workout_id]: { ...workout, exerciseIds: result }
      },
      exercises: newExercises
    };
  }),
  
  moveExerciseBetweenWorkouts: (sourceWorkoutId, destWorkoutId, sourceIndex, destIndex) => set((state) => {
    const sourceWorkout = state.workouts[sourceWorkoutId];
    const destWorkout = state.workouts[destWorkoutId];
    if (!sourceWorkout || !destWorkout) return state;
    
    if (sourceWorkoutId === destWorkoutId) {
       return get().reorderExercises(sourceWorkoutId, sourceIndex, destIndex);
    }
    
    const sourceExerciseIds = Array.from(sourceWorkout.exerciseIds);
    const destExerciseIds = Array.from(destWorkout.exerciseIds);
    
    const [movedExId] = sourceExerciseIds.splice(sourceIndex, 1);
    destExerciseIds.splice(destIndex, 0, movedExId);
    
    const newExercises = { ...state.exercises };
    sourceExerciseIds.forEach((id, i) => { if(newExercises[id]) newExercises[id].order = i + 1; });
    destExerciseIds.forEach((id, i) => { if(newExercises[id]) newExercises[id].order = i + 1; });

    return {
      workouts: {
        ...state.workouts,
        [sourceWorkoutId]: { ...sourceWorkout, exerciseIds: sourceExerciseIds },
        [destWorkoutId]: { ...destWorkout, exerciseIds: destExerciseIds }
      },
      exercises: newExercises
    };
  }),

  // Actions - Sets
  addSet: (exercise_instance_id) => set((state) => {
    const ex = state.exercises[exercise_instance_id];
    if (!ex) return state;
    
    const set_id = generateId();
    const lastSetId = ex.setIds[ex.setIds.length - 1];
    const lastSet = lastSetId ? state.sets[lastSetId] : null;
    
    const newSet = {
      set_id,
      set_index: ex.setIds.length + 1,
      set_type: lastSet ? lastSet.set_type : 'working',
      min_reps: lastSet ? lastSet.min_reps : 8,
      max_reps: lastSet ? lastSet.max_reps : 10,
      target_rpe: lastSet ? lastSet.target_rpe : 5,
      target_weight: lastSet ? lastSet.target_weight : ''
    };

    return {
      exercises: {
        ...state.exercises,
        [exercise_instance_id]: { ...ex, setIds: [...ex.setIds, set_id] }
      },
      sets: { ...state.sets, [set_id]: newSet }
    };
  }),
  
  updateSet: (set_id, updates) => set((state) => ({
    sets: {
      ...state.sets,
      [set_id]: { ...state.sets[set_id], ...updates }
    }
  })),

  removeSet: (exercise_instance_id, set_id) => set((state) => {
    const ex = state.exercises[exercise_instance_id];
    if (!ex) return state;
    
    const newSetIds = ex.setIds.filter(id => id !== set_id);
    const { [set_id]: removedSet, ...remainingSets } = state.sets;
    
    // Update indices for remaining sets
    newSetIds.forEach((id, idx) => {
      if (remainingSets[id]) {
        remainingSets[id] = { ...remainingSets[id], set_index: idx + 1 };
      }
    });

    return {
      exercises: {
        ...state.exercises,
        [exercise_instance_id]: { ...ex, setIds: newSetIds }
      },
      sets: remainingSets
    };
  }),

  duplicateSet: (exercise_instance_id, set_id) => set((state) => {
    const ex = state.exercises[exercise_instance_id];
    const original = state.sets[set_id];
    if (!ex || !original) return state;

    const newSetId = generateId();
    const insertAt = ex.setIds.indexOf(set_id);
    const newSetIds = [...ex.setIds];
    newSetIds.splice(insertAt + 1, 0, newSetId);

    const remainingSets = {
      ...state.sets,
      [newSetId]: { ...original, set_id: newSetId }
    };
    newSetIds.forEach((id, idx) => {
      if (remainingSets[id]) remainingSets[id] = { ...remainingSets[id], set_index: idx + 1 };
    });

    return {
      exercises: {
        ...state.exercises,
        [exercise_instance_id]: { ...ex, setIds: newSetIds }
      },
      sets: remainingSets
    };
  }),

  reorderSets: (exercise_instance_id, startIndex, endIndex) => set((state) => {
    const ex = state.exercises[exercise_instance_id];
    if (!ex) return state;
    if (startIndex === endIndex || startIndex < 0 || endIndex < 0) return state;

    const result = Array.from(ex.setIds);
    const [removed] = result.splice(startIndex, 1);
    result.splice(endIndex, 0, removed);

    const newSets = { ...state.sets };
    result.forEach((id, i) => {
      if (newSets[id]) newSets[id] = { ...newSets[id], set_index: i + 1 };
    });

    return {
      exercises: {
        ...state.exercises,
        [exercise_instance_id]: { ...ex, setIds: result }
      },
      sets: newSets
    };
  }),

  // UI State
  toggleWorkoutExpanded: (workout_id) => set((state) => ({
    ui: {
      ...state.ui,
      expandedWorkoutId: state.ui.expandedWorkoutId === workout_id ? null : workout_id
    }
  })),

  openWorkoutDrawer: (workout_id, options = {}) => set((state) => ({
    ui: {
      ...state.ui,
      editingWorkoutId: workout_id,
      searchOpenForWorkoutId: options.search ? workout_id : null,
      expandedExerciseId: options.search ? null : state.ui.expandedExerciseId,
      drawerLeaf: 'workout'
    }
  })),

  closeWorkoutDrawer: () => set((state) => ({
    ui: { ...state.ui, editingWorkoutId: null, searchOpenForWorkoutId: null, expandedExerciseId: null, drawerLeaf: 'workout' }
  })),

  openExerciseSettings: (exercise_instance_id) => set((state) => ({
    ui: { ...state.ui, expandedExerciseId: exercise_instance_id, searchOpenForWorkoutId: null, drawerLeaf: 'exercise' }
  })),

  closeExerciseSettings: () => set((state) => ({
    ui: { ...state.ui, expandedExerciseId: null, drawerLeaf: 'workout' }
  })),

  showWorkoutLeaf: () => set((state) => ({
    ui: { ...state.ui, drawerLeaf: 'workout', searchOpenForWorkoutId: null }
  })),

  showExerciseLeaf: () => set((state) => (
    state.ui.expandedExerciseId
      ? { ui: { ...state.ui, drawerLeaf: 'exercise', searchOpenForWorkoutId: null } }
      : state
  )),
  
  toggleExerciseExpanded: (exercise_instance_id) => set((state) => {
    const closing = state.ui.expandedExerciseId === exercise_instance_id;
    return {
      ui: {
        ...state.ui,
        expandedExerciseId: closing ? null : exercise_instance_id,
        searchOpenForWorkoutId: null,
        drawerLeaf: closing ? 'workout' : 'exercise'
      }
    };
  }),

  openSearch: (workout_id) => set((state) => ({
    ui: { ...state.ui, searchOpenForWorkoutId: workout_id }
  })),
  
  closeSearch: () => set((state) => ({
    ui: { ...state.ui, searchOpenForWorkoutId: null }
  }))
}));

export default useTrainingPlanStore;